// Turnstile.js — Widget de Cloudflare Turnstile («¿eres una persona?»).
//
// Lo usa el ingreso por correo hacia otra plataforma (modo `sso`), igual que el
// plugin de WordPress: el token que devuelve viaja al servidor, que lo verifica
// contra Cloudflare antes de pedir el acceso.
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Caja = styled.div`
  margin: 4px 0 2px;
  min-height: 65px;
`;

// El script se carga una sola vez por página.
let promesa = null;
const cargarScript = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!promesa) {
    promesa = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.defer = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return promesa;
};

/** Estado del token para quien use el widget: `reinicio` lo vuelve a pedir. */
// Se llama `useTurnstile` porque es un hook: React exige ese prefijo.
export const useTurnstile = () => {
  const [token, setToken] = useState('');
  const [reinicio, setReinicio] = useState(0);
  return {
    token,
    setToken,
    reinicio,
    reiniciar: () => { setToken(''); setReinicio(n => n + 1); },
  };
};

const Turnstile = ({ siteKey, onToken, onError, reinicio = 0, idioma = 'es' }) => {
  const caja = useRef(null);
  const widget = useRef(null);

  useEffect(() => {
    if (!siteKey || !caja.current) return undefined;
    let vivo = true;
    cargarScript().then((api) => {
      if (!vivo || !api || !caja.current) return;
      caja.current.innerHTML = '';
      widget.current = api.render(caja.current, {
        sitekey: siteKey,
        language: idioma,
        callback: (t) => onToken?.(t),
        'expired-callback': () => onToken?.(''),
        // 110200 = Cloudflare no tiene este dominio en la lista del widget.
        'error-callback': (codigo) => { onToken?.(''); onError?.(codigo); },
      });
    }).catch(() => {});
    return () => {
      vivo = false;
      try { if (widget.current && window.turnstile) window.turnstile.remove(widget.current); } catch (e) { /* ignorado */ }
      widget.current = null;
    };
  // Con `reinicio` se pinta de nuevo tras un error, para pedir otra verificación.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, idioma, reinicio]);

  if (!siteKey) return null;
  return <Caja ref={caja} />;
};

export default Turnstile;
